import React, { useState, useEffect } from 'react'
import { FaBoxOpen } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { db } from '../../../firebase/Config'
import { collection, getDocs, updateDoc, doc, runTransaction } from 'firebase/firestore'
import LoadingForAll from '../../../Pages/LoadingForAll'
import './OrdersAll.css'

function OrdersAll() {
    const navigate = useNavigate()
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [shippingLoading, setShippingLoading] = useState({})
    useEffect(() => {
        const fetchOrders = async () => {
            setLoading(true)
            setError(null)
            try {
                const querySnapshot = await getDocs(collection(db, 'orders'))
                const orders = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }))
                orders.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
                setOrders(orders);
            } catch (error) {
                setError(error.message)
            } finally {
                setLoading(false)
            }
        }
        fetchOrders()
    }, [])

    const handleShipOrder = async (orderId, productId, index) => {
        const loadingKey=`${orderId}_${productId}`
        setShippingLoading(prev=>({...prev, [loadingKey]:true}))
        try {
            await runTransaction(db, async (transaction) => {
                // 1. Update the productIsShipped status for this specific product in the order
                const orderRef = doc(db, 'orders', orderId)
                const orderDoc = await transaction.get(orderRef)
                if (!orderDoc.exists()) {
                    throw new Error("Order document does not exist!")
                }

                const orderData = orderDoc.data()
                const updatedProducts = [...orderData.productsDetails]
                updatedProducts[index].productIsShipped = true

                // 2. Update the product stock and totalOrders
                const productRef = doc(db, 'products', productId)
                const productDoc = await transaction.get(productRef)
                if (!productDoc.exists()) {
                    throw new Error("Product document does not exist!")
                }

                const productData = productDoc.data()
                const currentStock = parseInt(productData.stock) || 0
                const currentTotalOrders = parseInt(productData.totalOrders) || 0
                const quantity = updatedProducts[index].productQuantity

                // 3. Check if all products in the order are shipped
                const allShipped = updatedProducts.every(p => p.productIsShipped)

                // Perform all updates in the transaction
                transaction.update(productRef, {
                    stock: (currentStock - quantity).toString(),
                    totalOrders: (currentTotalOrders + quantity).toString()
                })

                transaction.update(orderRef, {
                    productsDetails: updatedProducts,
                    orderStatus: allShipped ? 'shipped' : orderData.orderStatus
                })
            })

            // Update local state to reflect changes
            setOrders(prevOrders => 
                prevOrders.map(order => {
                    if (order.id === orderId) {
                        const updatedProducts = [...order.productsDetails]
                        updatedProducts[index].productIsShipped = true
                        
                        const allShipped = updatedProducts.every(p => p.productIsShipped)
                        
                        return {
                            ...order,
                            productsDetails: updatedProducts,
                            orderStatus: allShipped ? 'shipped' : order.orderStatus
                        }
                    }
                    return order
                })
            )
        } catch (error) {
            console.error("Error shipping order:", error)
            setError(error.message)
        } finally {
            setShippingLoading(prev=>({...prev, [loadingKey]:false}))
        }
    }

    if (loading) {
        return <LoadingForAll title='Loading Orders...'></LoadingForAll>
    }
    if (error) {
        return <div>Error: {error}</div>
    }
    
    return (
        <div className="admin-orders-dashboard">
        <div className="admin-orders-topbar">
          <h1 className="admin-orders-main-title">Order Management</h1>
          <p className="admin-orders-description">Manage and fulfill customer orders</p>
          <div className="admin-title-decoration"></div>
        </div>
      
        {orders.length === 0 ? (
          <div className="admin-empty-state">
            <div className="empty-state-icon-container">
              <FaBoxOpen className="empty-state-symbol" />
            </div>
            <h2 className="empty-state-heading">No Orders to Display</h2>
            <p className="empty-state-text">When customers place orders, they'll appear here for processing.</p>
          </div>
        ) : (
          <div className="admin-orders-listing">
            {orders.map((order) => (
              <div className="admin-order-panel" key={order.id}>
                <div className="order-panel-header">
                  <div className="order-meta-container">
                    <div className="order-meta-element">
                      <svg className="meta-element-icon" viewBox="0 0 24 24">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{new Date(order.orderDate).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</span>
                    </div>
                   
                  </div>
                  
                  <div className="order-status-container">
                    <span className={`order-status-indicator ${order.orderStatus.toLowerCase()}`}>
                      {order.orderStatus}
                    </span>
                    <span className="order-total-amount">₹{order.totalAmount?.toLocaleString()}</span>
                  </div>
                </div>
      
                <div className="order-content-section">
                  <div className="customer-info-block">
                    <h3 className="section-heading">
                      <svg className="section-heading-icon" viewBox="0 0 24 24">
                        <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Customer Information
                    </h3>
                    <div className="customer-details-grid">
                      <div className="customer-detail-item">
                        <strong>Name:</strong> {order.name}
                      </div>
                      <div className="customer-detail-item">
                        <strong>Email:</strong> {order.email}
                      </div>
                      <div className="customer-detail-item">
                        <strong>Phone:</strong> {order.phone}
                      </div>
                      <div className="customer-detail-item full-width">
                        <strong>Address:</strong> {order.address}, {order.city}, {order.state} - {order.pincode}
                      </div>
                    </div>
                  </div>
      
                  <div className="products-info-block">
                    <h3 className="section-heading">
                      <svg className="section-heading-icon" viewBox="0 0 24 24">
                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      Products ({order.productsDetails?.length || 0})
                    </h3>
                    
                    {order.productsDetails?.length > 0 ? (
                      <div className="admin-order-products-grid">
                      {order.productsDetails.map((product, index) => (
                        <div className="admin-order-product-card" key={index}>
                          <div 
                            className="admin-product-image-box"
                            onClick={() => navigate(`/view-product-details/${product.productId}`)}
                            style={{ backgroundImage: `url(${product.productImage})` }}
                          ></div>
                          
                          <div className="admin-product-details-box">
                            <h4 
                              className="admin-product-name"
                              onClick={() => navigate(`/view-product-details/${product.productId}`)}
                            >
                              {product.productName}
                            </h4>
                            
                            <div className="admin-product-meta-grid">
                              <div className="admin-product-price">
                                <strong>Price:</strong> ₹{product.productPrice?.toLocaleString()}
                              </div>
                              <div className="admin-product-quantity">
                                <strong>Qty:</strong> {product.productQuantity}
                              </div>
                              <div className={`admin-product-status ${product.productIsShipped ? 'admin-shipped' : 'admin-pending'}`}>
                                {product.productIsShipped ? (
                                  <>
                                    <svg className="admin-status-icon" viewBox="0 0 24 24">
                                      <path d="M5 13l4 4L19 7" />
                                    </svg>
                                    Shipped
                                  </>
                                ) : (
                                  <>
                                    <svg className="admin-status-icon" viewBox="0 0 24 24">
                                      <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Pending
                                  </>
                                )}
                              </div>
                            </div>
                            
                            <div className="admin-product-actions">
                              <button 
                                className="admin-view-details-btn"
                                onClick={() => navigate(`/view-product-details/${product.productId}`)}
                              >
                                View Details
                              </button>
                              <button
                                className={`admin-ship-btn ${product.productIsShipped ? 'admin-shipped-state' : ''}`}
                                disabled={product.productIsShipped || shippingLoading[`${order.id}_${product.productId}`]}
                                onClick={() => handleShipOrder(order.id, product.productId, index)}
                              >
                                {shippingLoading[`${order.id}_${product.productId}`] ? (
                                  <span className="admin-loading-spinner"></span>
                                ) : product.productIsShipped ? (
                                  'Shipped'
                                ) : (
                                  'Mark as Shipped'
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    ) : (
                      <div className="no-products-message">
                        No products found in this order
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
}

export default OrdersAll